import React, { useMemo } from "react";
import { useLayout } from "../../Layouts/LayoutContext";
import PanelCanvas from "../../PanelLayout/PanelLayoutComponents/PanelCanvas";
import watermark from "../../../Assets/eplan_watermark.png"
import "../../Layouts/Layout.css";

export default function ViewerPanel() {
  const L = useLayout();
  const p = L.selectedProject;

  // Gray/disabled during the whole calculation: backend placement AND the
  // 3D scene rebuild after a manual recalculation.
  const busy = L.isCalculating || L.isManualCalculating;

  // same base snapshot as before: cabinet + ducts even before sections are generated
  const baseSnapshot = useMemo(() => {
    const ep = L.enclosurePlacement;
    if (!ep) return null;
    const vWidth = Number(ep.ductLeft?.size?.width ?? ep.ductRight?.size?.width ?? 0) || 0;
    const topWidth = Math.max(Number(ep.size?.width ?? 0) - 2 * vWidth, 0);
    const topHeight = Number(L.sectionPlacements?.[0]?.ductTop?.size?.height ?? 40) || 40;
    return {
      cabinet: ep,
      montagePlate: ep.montagePlacement ?? null,
      ductLeft: ep.ductLeft ?? null,
      ductRight: ep.ductRight ?? null,
      ductTopEnclosure: ep.ductTop ?? null,
      ductTop: { size: { width: topWidth, height: topHeight } },
      devices: ep.devices ?? [],
      generatedSections: ep.sectionPlacements ?? [],
    };
  }, [L.enclosurePlacement]);

  const snapshot = L.canvasSnapshot ?? baseSnapshot;

  // One button, used in the canvas toolbar AND in the "Enclosure too small" box
  const recalcButton = (extraClass = "") => (
    <button
      className={`pl-primary${L.layoutOutdated && !busy ? " pl-attention" : ""}${extraClass ? ` ${extraClass}` : ""}`}
      disabled={!L.hasEnclosure || busy}
      onClick={L.calculateLayout}
    >
      {busy ? "Calculating…" : "Recalculate layout"}
    </button>
  );

  const canvas = useMemo(() => {
    if (!snapshot) return null;
    return (
      <PanelCanvas
        snapshot={snapshot}
        version={L.canvasVersion}
        optimized={L.useOptimized}
        sectionPlacements={p?.enclosurePlacement?.sectionPlacements}
        sections={L.selectedScenario?.sections}
        projectId={p?.id}
        devices={p?.enclosurePlacement?.devices}
        onLoadingChange={L.setIsCanvasLoading}
        onQuantityChange={L.handleQuantityChange}
        onPlaceholderQuantityChange={L.handlePlaceholderQuantityChange}
        onDeleteDevice={L.handleDeleteDevice}
        // Shared hide/select state — same ids drive the 2D canvas, the 3D
        // canvas, and the tree, whichever of them is actually mounted.
        hiddenIds={L.hiddenIds}
        selectedId={L.selectedId}
        onSelect={L.selectId}
        // PanelCanvasReal3D reports cabinetNodes/deviceNodeMap/onFit up
        // here (only it needs to, since only it has to translate ids into
        // real scene-node visibility/highlight).
        onSceneReady={L.setSceneHandlers}
        toolbarLeft={recalcButton()}
        onViewModeChange={L.reportViewMode}
      />
    );
    // busy / layoutOutdated / hasEnclosure are needed here: the button lives
    // inside this memoized element and must re-render when they change
    // (otherwise it does not turn gray while calculating).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, L.canvasVersion, L.useOptimized, L.selectedScenario?.sections, L.setIsCanvasLoading, L.handleQuantityChange, L.handlePlaceholderQuantityChange, L.handleDeleteDevice, L.hiddenIds, L.selectedId, L.selectId, L.setSceneHandlers, p?.enclosurePlacement?.sectionPlacements, p?.enclosurePlacement?.devices, p?.id, busy, L.layoutOutdated, L.hasEnclosure, L.calculateLayout, L.reportViewMode]);

  if (!L.hasEnclosure) return <div className="pl-empty">Select an enclosure to see the layout.</div>;

  return (
    <div className="pl-viewer">
      {/* The canvas stays mounted while the enclosure is too small: the
          2D/3D choice and the 3D engine survive, and nothing has to be
          rebuilt from scratch once the layout fits again. */}
      {L.isManualCalculating && <div className="canvas-loading-overlay"><div className="canvas-loading-spinner" /></div>}
      {canvas}

      {L.enclosureTooSmall && (
        <div className="pl-too-small-overlay">
          <div className="pl-alert pl-alert-error" role="alert">
            <h3>Enclosure too small</h3>
            <p>Required height: <b>{L.requiredHeight} mm</b><br />
              Current mounting plate height: <b>{p?.enclosurePlacement?.montagePlacement?.size?.height} mm</b></p>
            <p>Choose a larger enclosure in the Enclosure tab, or reduce the devices,
              then recalculate the layout.</p>
            <div className="pl-alert-actions">
              {recalcButton("pl-alert-btn")}
              <button
                className="pl-secondary pl-alert-btn"
                onClick={L.clearEnclosureBlock}
                disabled={busy}
                title="Hide this message and show the layout that does not fit"
              >
                Show layout anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <img className="pl-watermark" src={watermark} alt="" aria-hidden="true" draggable={false} />
    </div>
  );
}
